import {
  debounceTime,
  filter,
  map,
  switchMap,
  catchError,
} from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, signal, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormsModule,
  AbstractControl,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { Select } from 'primeng/select';
import { ProfileService } from '../../services/profile/profile.service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { finalize, tap, of, EMPTY, Observable, Subscription, timer } from 'rxjs';
import { ApiResponse, Company, UserProfile } from '../../../types/commons';
import { AuthService } from '../../services/auth/auth.service';
import { AuthFailureNoticeService } from '../../services/auth/auth-failure-notice.service';
import { RuntimeConfigService } from '../../services/common/runtime-config.service';
import { UserManagementService } from '../../services/user-management/user-management.service';
import { normalizeApiErrors } from '../../utils/api-errors.util';
import {
  baselineRole,
  buildChangePasswordRequest,
  buildUpdateUserRequest,
  computeChangeSet,
  ProfileFormRaw,
} from './profile-change-set.util';
import { Router } from '@angular/router';
import { parseUserRole, roleDisplayName, UserRole } from '../../../types/user-role';
import {
  passwordFieldValidator,
  passwordMatchValidator,
  passwordNotSameValidator,
  PasswordRule,
} from '../../utils/helpers';
import { newPasswordPolicyValidator, PASSWORD_POLICY_RULES } from '../../utils/password-policy.util';
import {
  CHANGE_PASSWORD_COOLDOWN_MS,
  ChangePasswordFailure,
  resolveChangePasswordFailure,
} from '../../utils/change-password-errors.util';
import {
  ASYNC_VALIDATION_DEBOUNCE_MS,
  CURRENT_PASSWORD_VERIFY_DEBOUNCE_MS,
} from '../../utils/debounce-times.util';
import {
  CurrentPasswordVerdict,
  CurrentPasswordVerificationService,
} from '../../services/auth/current-password-verification.service';
import { InteractiveToastComponent } from '../common/interactive-toast/interactive-toast.component';
import { TooltipModule } from 'primeng/tooltip';
import { emailValidator } from '../../app.validators';
import {
  TOAST_DURATION_LONG_MS,
} from '../../utils/toast-durations.util';
import { DIAGNOSTIC_TOAST_LIFE_MS } from '../../utils/sheet-load-trace.util';
import { AccessDeniedNoticeService } from '../../services/common/access-denied-notice.service';
import { buildPageLoadFailureNotice } from '../../utils/page-load-failure.util';
import { PageLoadFailureToastGate } from '../../utils/page-load-failure-toast-gate.util';

@Component({
  selector: 'profile',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CardModule,
    InputTextModule,
    Select,
    PasswordModule,
    ButtonModule,
    TooltipModule,
    InteractiveToastComponent,
    ConfirmDialogModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './profile.component.html',
})
export class ProfileComponent implements OnInit {
  /** Label of the Company control for a SuperAdmin, who belongs to no single company. */
  private static readonly ALL_COMPANIES_LABEL = 'All Companies';

  /**
   * Value of the Company control's one option. Company is display-only here
   * (always disabled, never in the change-set or the payload), and since
   * 2026-10-20 the profile carries only the company NAME, no id and no list, so
   * the option is synthetic: it exists only to give the dropdown a label.
   */
  private static readonly COMPANY_OPTION_ID = '';

  passwordRules = signal<PasswordRule[]>([]);
  profileData = signal<UserProfile | null>(null);

  /**
   * The Company dropdown's single option, from the profile's own `company`.
   * "All Companies" is keyed on the SuperAdmin ROLE (the backend sends such a
   * profile `company: null`, and `''` before 2026-10-20), never on a missing
   * company alone: a User or Admin with no company assigned (a data fault) also
   * gets `company: null` and must not read as "All Companies"; it shows nothing.
   */
  companies = computed<Company[]>(() => {
    const profile = this.profileData();
    if (!profile) {
      return [];
    }
    if (this.profileRole() === UserRole.SuperAdmin) {
      return [{ id: ProfileComponent.COMPANY_OPTION_ID, name: ProfileComponent.ALL_COMPANIES_LABEL }];
    }
    const companyName = profile.company?.trim() ?? '';
    return companyName === '' ? [] : [{ id: ProfileComponent.COMPANY_OPTION_ID, name: companyName }];
  });
  isSubmitting = signal<boolean>(false);
  isProfileDataLoading = signal<boolean>(false);

  isPendingChanges = signal<boolean>(false);
  profileDataService = inject(ProfileService);
  authService = inject(AuthService);
  authFailureNotice = inject(AuthFailureNoticeService);
  accessDeniedNotice = inject(AccessDeniedNoticeService);
  messageService = inject(MessageService);
  userManagementService = inject(UserManagementService);
  confirmationService = inject(ConfirmationService);
  currentPasswordVerification = inject(CurrentPasswordVerificationService);
  router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * True for `CHANGE_PASSWORD_COOLDOWN_MS` after the server rate-limits the
   * password change. In-memory on purpose: a refresh resets it (the server
   * still enforces the limit), and nothing about it belongs in web storage.
   */
  isPasswordCooldownActive = signal<boolean>(false);
  private passwordCooldownTimer: Subscription | null = null;
  private originalEmail: string = '';
  // Caps this page's load-failure toast to one per class — see
  // PageLoadFailureToastGate's own docblock. Applied uniformly across every
  // page-load-failure.util.ts consumer, not only the pages with more than
  // one load-up API call, so the same class of bug can't resurface quietly
  // the moment a second call is added here.
  // REFACTORED-DIAGNOSTIC-CODES-FLAG: page-level toasts are silent in production.
  private readonly runtimeConfig = inject(RuntimeConfigService);
  private readonly loadFailureToastGate = new PageLoadFailureToastGate(undefined, () =>
    this.runtimeConfig.refactoredDiagnosticCodesEnabled(),
  );

  /** The role of the account whose profile is on screen, from the loaded profile. */
  profileRole = computed(() => parseUserRole(this.profileData()?.roles?.[0]));

  /**
   * Name is read-only when the viewer cannot manage users, or when
   * the profile is a SuperAdmin's (who can manage users but has no editable
   * self-row). Role and Company are NOT governed by this: Company is never
   * self-editable (company moves stay SuperAdmin-only, in User Management), and
   * Role has its own rule, `canSelfDowngrade`.
   */
  isDisabled = computed(
    () => !this.authService.canManageUsers() || this.profileRole() === UserRole.SuperAdmin
  );

  /**
   * An Admin may downgrade THEMSELVES to User here. It mirrors the rule that an
   * Admin may edit a fellow Admin of the same company. The P.O. accepted the
   * awkward result (an Admin can demote themselves, and must then ask another
   * Admin to restore the role) as a revisit for a later ticket: AS-1387 / AS-1388. A User
   * and a SuperAdmin keep the Role control locked. The backend must allow this
   * self-change too (it refused it with 403 `ROLE_CHANGE_SUPERADMIN_ONLY` as of
   * 2026-10-20); a refusal surfaces as the ordinary save-failure toast.
   */
  canSelfDowngrade = computed(
    () => this.authService.role() === UserRole.Admin && this.profileRole() === UserRole.Admin
  );

  /** Tracks the loaded role, so a save can tell a self-downgrade from any other edit. */
  private originalRole = signal<UserRole | ''>('');

  /** A SuperAdmin's role is shown as-is; everyone else picks between Admin and User. */
  roleOptions = computed<{ label: string; value: string }[]>(() =>
    (this.profileRole() === UserRole.SuperAdmin
      ? [UserRole.SuperAdmin]
      : [UserRole.Admin, UserRole.User]
    ).map((role) => ({ label: roleDisplayName(role), value: role }))
  );

  ngOnInit(): void {
    this.resetForm();
  }

  fetchProfileData() {
    this.isProfileDataLoading.set(true);
    this.profileDataService
      .fetchProfileData()
      .pipe(finalize(() => this.isProfileDataLoading.set(false)))
      .subscribe({
        next: (data) => {
          this.profileData.set(data);
          this.originalEmail = (data.email ?? '').trim();
          const currentRole = baselineRole(data);
          this.originalRole.set(currentRole);
          this.syncControlDisabledState();
          if (data.pendingChanges.length > 0) {
            if (data.pendingChanges.includes(data.id)) {
              this.isPendingChanges.set(true);
            } else {
              this.isPendingChanges.set(false);
            }
          }
          this.profileForm.patchValue({
            inputName: data.name,
            inputEmail: data.email,
            selectedCompany: ProfileComponent.COMPANY_OPTION_ID,
            selectedRole: currentRole,
          });
        },
        error: (error: unknown) => {
          const notice = buildPageLoadFailureNotice('PROFILE', error);
          if (notice.failure === 'PERM') {
            // Not allowed to see this page's data: leave for the Dashboard with the shell toast.
            this.accessDeniedNotice.denyPageAccess();
            return;
          }
          if (notice.silent) {
            // AUTH: this page is about to be navigated away from by the
            // forced-logout redirect — stage the composed summary for the login page.
            this.authFailureNotice.stage(notice.summary);
          } else if (this.loadFailureToastGate.shouldToast(notice.failure)) {
            this.messageService.add({
              key: 'profile',
              severity: 'error',
              summary: notice.summary,
              life: DIAGNOSTIC_TOAST_LIFE_MS,
            });
          }
        },
      });
  }

  constructor() {
    this.passwordRules.set(PASSWORD_POLICY_RULES);
    this.fetchProfileData();
    this.setupEmailDuplicateCheck();
    this.setupCurrentPasswordCheck();
  }

  profileForm = new FormGroup(
    {
      inputName: new FormControl({ value: '', disabled: this.isDisabled() }, [
        Validators.required,
      ]),
      inputEmail: new FormControl({ value: '', disabled: true }, [
        Validators.required,
        emailValidator(),
      ]),
      selectedCompany: new FormControl(
        { value: '', disabled: true },
        Validators.required
      ),
      inputCurrentPassword: new FormControl(''),
      inputNewPassword: new FormControl('', newPasswordPolicyValidator),
      inputConfirmPassword: new FormControl(''),
      selectedRole: new FormControl(
        { value: '', disabled: true },
        Validators.required
      ),
    },
    {
      validators: [
        passwordMatchValidator,
        passwordFieldValidator,
        passwordNotSameValidator,
      ],
    }
  );

  get inputName() {
    return this.profileForm.get('inputName');
  }

  get inputEmail() {
    return this.profileForm.get('inputEmail');
  }

  get selectedCompany() {
    return this.profileForm.get('selectedCompany');
  }

  get selectedRole() {
    return this.profileForm.get('selectedRole');
  }

  get inputCurrentPassword() {
    return this.profileForm.get('inputCurrentPassword');
  }

  get inputNewPassword() {
    return this.profileForm.get('inputNewPassword');
  }

  get inputConfirmPassword() {
    return this.profileForm.get('inputConfirmPassword');
  }

  /**
   * The form is built before the profile arrives, so the states that depend on
   * the loaded role (a SuperAdmin's read-only Name/Email, an Admin's editable
   * Role) are applied here once it is known. Company stays disabled throughout.
   * `emitEvent: false` keeps the email duplicate-check pipeline quiet.
   */
  private syncControlDisabledState(): void {
    if (this.isDisabled()) {
      this.inputName?.disable({ emitEvent: false });
    } else {
      this.inputName?.enable({ emitEvent: false });
    }
    // Email is read-only for every role on My Profile (P.O., 2026-10-27).
    this.inputEmail?.disable({ emitEvent: false });
    if (this.canSelfDowngrade()) {
      this.selectedRole?.enable({ emitEvent: false });
    } else {
      this.selectedRole?.disable({ emitEvent: false });
    }
  }

  closeConfirmationDialog(): void {
    this.confirmationService.close();
  }

  resetForm() {
    if (this.profileData()) {
      this.profileForm.reset({
        inputName: this.profileData()?.name,
        inputEmail: this.profileData()?.email,
        selectedCompany: ProfileComponent.COMPANY_OPTION_ID,
        selectedRole: baselineRole(this.profileData()),
        inputCurrentPassword: '',
        inputNewPassword: '',
        inputConfirmPassword: '',
      });
    } else {
      this.profileForm.reset({
        inputName: '',
        inputEmail: '',
        selectedCompany: '',
        selectedRole: '',
        inputCurrentPassword: '',
        inputNewPassword: '',
        inputConfirmPassword: '',
      });
    }
    this.profileForm.markAsPristine();
    this.profileForm.markAsUntouched();
  }

  isButtonDisabled(): boolean {
    return (
      this.profileForm.invalid ||
      this.profileForm.pending ||
      this.isSubmitting() ||
      this.isFieldInvalid() ||
      this.isPasswordSaveBlockedByCooldown()
    );
  }

  /**
   * During the rate-limit cooldown only a save that would CALL change-password
   * is blocked; a profile-only edit (name, email ...) never touches that
   * endpoint and stays savable.
   */
  private isPasswordSaveBlockedByCooldown(): boolean {
    return (
      this.isPasswordCooldownActive() &&
      computeChangeSet(this.profileForm.getRawValue(), this.profileData()).passwordTouched
    );
  }

  isFieldInvalid(): boolean {
    if (!this.profileData()) {
      return true;
    }

    const { profileChanged, passwordTouched } = computeChangeSet(
      this.profileForm.getRawValue(),
      this.profileData()
    );

    return !(profileChanged || passwordTouched);
  }

  submitForm() {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }

    if (this.isPasswordSaveBlockedByCooldown()) {
      return;
    }

    const rawFormValue = this.profileForm.getRawValue();
    const isDowngrading =
      this.originalRole() === UserRole.Admin && rawFormValue.selectedRole === UserRole.User;

    if (isDowngrading) {
      // Ending your own Admin rights signs you out, so ask first (AS-1387 / AS-1388).
      this.confirmationService.confirm({
        accept: () => this.proceedWithUpdate(rawFormValue, isDowngrading),
      });
      return;
    }
    this.proceedWithUpdate(rawFormValue, isDowngrading);
  }

  /**
   * Saving this page can touch two endpoints, and they are NOT transactional,
   * so the order and the failure handling are deliberate:
   *
   *   1. `POST /user/change-password` runs FIRST. Password-first is
   *      load-bearing: a role change ends the session and a password change
   *      does not, so an Admin downgrading themselves to User would end the
   *      session mid-save, and profile-first ordering would lose the password
   *      call to it. Password-first introduces no failure mode of its own; a
   *      failed password aborts the profile edit.
   *   2. `PUT /user/update-user` runs only if the password call succeeded (or
   *      was not needed).
   *
   * A User-role account never reaches step 2: name, email and role are disabled
   * for them (`isDisabled`, `canSelfDowngrade`) and company always is, so
   * `profileChanged` is always false and the Admin-only 403 is unreachable from
   * this page.
   */
  private proceedWithUpdate(rawFormValue: ProfileFormRaw, isDowngrading: boolean) {
    const { profileChanged, passwordChanged } = computeChangeSet(
      rawFormValue,
      this.profileData()
    );

    // Defensive only, not a live path: `submitForm` already requires a valid
    // form, and `passwordFieldValidator` invalidates a partially-filled
    // password set — so one of these two flags is always true by here.
    if (!profileChanged && !passwordChanged) {
      return;
    }

    this.isSubmitting.set(true);

    // Annotated because the ternary would otherwise widen to `{}` — `of(null)`
    // is the "no password change" no-op arm, not a second response shape.
    const passwordCall: Observable<ApiResponse | null> = passwordChanged
      ? this.authService.changePassword(buildChangePasswordRequest(rawFormValue))
      : of(null);

    passwordCall
      .pipe(
        switchMap((passwordResponse) => {
          if (passwordResponse && !passwordResponse.status) {
            // Abort: do not apply profile edits behind a rejected password.
            this.showPasswordFailure(passwordResponse);
            return EMPTY;
          }

          if (!profileChanged) {
            this.onPasswordOnlySuccess();
            return EMPTY;
          }

          return this.userManagementService
            .updateUser(buildUpdateUserRequest(rawFormValue, this.profileData()))
            .pipe(
              tap((profileResponse) => {
                if (profileResponse.status) {
                  this.onProfileSuccess(isDowngrading, passwordChanged);
                  return;
                }

                if (passwordChanged) {
                  // The password already landed — say so, then report the
                  // half that did not. Two toasts, never one blended verdict.
                  this.messageService.add({
                    key: 'profile',
                    severity: 'custom-success',
                    summary: 'Your password has been changed.',
                    life: TOAST_DURATION_LONG_MS,
                  });
                  this.clearPasswordFields();
                }

                this.flagPendingChanges(profileResponse);
                this.messageService.add({
                  key: 'profile',
                  severity: 'custom-error',
                  summary: 'Failed to save changes. Please try again later.',
                  life: TOAST_DURATION_LONG_MS,
                });
              })
            );
        }),
        // `AuthService.handleError` deliberately RE-THROWS a network error
        // (status 0) instead of resolving it into a falsy-`status` envelope,
        // and `http-error.interceptor` skips its own toast + `/error`
        // navigation for `/api/auth/` URLs. Without this arm an offline
        // password change would fail completely silently — the Save button
        // would just re-enable via `finalize`.
        catchError(() => {
          this.messageService.add({
            key: 'profile',
            severity: 'custom-error',
            summary: 'Unable to reach the server. Please check your connection and try again.',
            life: TOAST_DURATION_LONG_MS,
          });
          return EMPTY;
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe();
  }

  private onPasswordOnlySuccess() {
    this.resetForm();
    this.messageService.add({
      key: 'profile',
      severity: 'custom-success',
      summary: 'Your password has been changed.',
      life: TOAST_DURATION_LONG_MS,
    });
  }

  private onProfileSuccess(isDowngrading: boolean, passwordChanged: boolean) {
    // Confirm the password half BEFORE the downgrade branch can redirect:
    // otherwise a save that both changed the password and downgraded the
    // account would sign the user out with no sign the password ever landed.
    if (passwordChanged) {
      this.messageService.add({
        key: 'profile',
        severity: 'custom-success',
        summary: 'Your password has been changed.',
        life: TOAST_DURATION_LONG_MS,
      });
    }

    if (isDowngrading) {
      // The role change takes effect on sign-out, so end the session here.
      // The redirect runs on BOTH arms: if sign-out itself fails the account
      // still holds a role the server has already changed, so leaving the
      // user on this page would strand them in a stale session.
      this.authService
        .logout()
        .pipe(finalize(() => this.router.navigate(['/auth/login'])))
        .subscribe({ error: () => {} });
      return;
    }

    this.fetchProfileData();
    this.resetForm();

    this.messageService.add({
      key: 'profile',
      severity: 'custom-success',
      summary: 'Your profile has been successfully saved.',
      life: TOAST_DURATION_LONG_MS,
    });
  }

  /**
   * `errors` is typed `string[]` but some routes answer with a bare string,
   * hence `normalizeApiErrors` rather than a direct `.length` read.
   */
  private showApiError(response: ApiResponse, fallback: string) {
    this.flagPendingChanges(response);

    const [firstError] = normalizeApiErrors(response.errors);

    this.messageService.add({
      key: 'profile',
      severity: 'custom-error',
      summary: firstError || fallback,
      life: TOAST_DURATION_LONG_MS,
    });
  }

  /**
   * Presents a failed change-password call. Each message gets its own toast
   * (the server can send several at once); a wrong current password also
   * raises the control's own `currentPasswordInvalid` error, which the
   * existing red border + message under that field already render.
   */
  private showPasswordFailure(response: ApiResponse) {
    this.flagPendingChanges(response);
    const failure: ChangePasswordFailure = resolveChangePasswordFailure(response);

    for (const message of failure.messages) {
      this.messageService.add({
        key: 'profile',
        severity: 'custom-error',
        summary: message,
        life: TOAST_DURATION_LONG_MS,
      });
    }

    if (failure.kind === 'incorrectCurrent') {
      this.setControlError(this.profileForm.controls.inputCurrentPassword, 'currentPasswordInvalid', true);
    } else if (failure.kind === 'rateLimited') {
      this.startPasswordCooldown();
    }
  }

  private startPasswordCooldown() {
    this.passwordCooldownTimer?.unsubscribe();
    this.isPasswordCooldownActive.set(true);
    this.passwordCooldownTimer = timer(CHANGE_PASSWORD_COOLDOWN_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.isPasswordCooldownActive.set(false));
  }

  private flagPendingChanges(response: ApiResponse) {
    const hasPending = normalizeApiErrors(response.errors).some((error) =>
      error.includes('User has pending changes')
    );

    if (hasPending) {
      this.isPendingChanges.set(true);
    }
  }

  private clearPasswordFields() {
    this.profileForm.patchValue({
      inputCurrentPassword: '',
      inputNewPassword: '',
      inputConfirmPassword: '',
    });
  }

  private setupEmailDuplicateCheck() {
    const emailCtrl = this.profileForm.get('inputEmail') as FormControl;

    emailCtrl.valueChanges
      .pipe(
        map((email) => (email ?? '').toString().toLowerCase()),

        tap((email) => {
          const orig = (this.originalEmail ?? '').trim().toLowerCase();
          if (email === orig) {
            this.setControlError(emailCtrl, 'emailTaken', false);
          } else if (
            !emailCtrl.disabled &&
            !emailCtrl.hasError('required') &&
            !emailCtrl.hasError('invalidEmailSyntax') &&
            !emailCtrl.hasError('email')
          ) {
            // Mark as pending immediately to prevent button from enabling during debounce
            emailCtrl.markAsPending();
          }
        }),

        filter(
          () =>
            !emailCtrl.disabled &&
            !emailCtrl.hasError('required') &&
            !emailCtrl.hasError('invalidEmailSyntax') &&
            !emailCtrl.hasError('email')
        ),
        debounceTime(ASYNC_VALIDATION_DEBOUNCE_MS),

        filter((email) => email !== (this.originalEmail ?? '').trim().toLowerCase()),

        switchMap((email) => {
          return this.userManagementService.checkEmailAvailability(email).pipe(
            // service returns: true => TAKEN, false => AVAILABLE
            catchError(() => of(false))
          );
        }),

        takeUntilDestroyed()
      )
      .subscribe((taken) => {
        this.setControlError(emailCtrl, 'emailTaken', taken);
      });
  }

  /**
   * Debounced check of the typed current password against the stored hash.
   *
   * The control has no local validator — correctness is not knowable on the
   * client — so this subscription is the only thing that can produce a
   * `currentPasswordInvalid` verdict. Two deliberate choices:
   *
   * - The stale verdict is cleared on the FIRST keystroke, not after the
   *   debounce. Leaving it set would keep a corrected password rendering as
   *   wrong for a further second.
   * - `distinctUntilChanged` is intentionally absent. It would suppress the
   *   re-check when a user edits away from a rejected password and back within
   *   one debounce window, leaving the field looking clean while still holding
   *   a password the server rejects. A duplicate request is the cheaper error.
   */
  private setupCurrentPasswordCheck() {
    const passwordCtrl = this.profileForm.get(
      'inputCurrentPassword'
    ) as FormControl;

    passwordCtrl.valueChanges
      .pipe(
        map((value) => (value ?? '').toString()),

        tap((value) => {
          this.setControlError(passwordCtrl, 'currentPasswordInvalid', false);

          // Lock Save from the FIRST keystroke, not when the request goes out.
          // Between the keystroke and the verdict the password's correctness is
          // unknown, and an unknown password must not be submittable — without
          // this the button stays clickable through the debounce window and the
          // round trip. `isButtonDisabled()` already reads `profileForm.pending`,
          // and marking the control pending propagates to the group.
          //
          // Skipped for an empty value: that means "not changing my password",
          // and no check is made, so there is nothing to wait for. The
          // setErrors(null) above has already cleared any earlier pending state.
          if (value) {
            passwordCtrl.markAsPending();
          }
        }),

        debounceTime(CURRENT_PASSWORD_VERIFY_DEBOUNCE_MS),

        // An empty field has nothing to verify — skip the round trip entirely
        // rather than spending a rate-limited request on a blank string.
        switchMap((value) =>
          value
            ? this.currentPasswordVerification.verify(value)
            : of<CurrentPasswordVerdict>('unknown')
        ),

        takeUntilDestroyed()
      )
      .subscribe((verdict) => {
        this.setControlError(
          passwordCtrl,
          'currentPasswordInvalid',
          verdict === 'invalid'
        );
      });
  }

  private setControlError(
    ctrl: AbstractControl,
    key: string,
    present: boolean
  ) {
    const errs = { ...(ctrl.errors ?? {}) };
    if (present) errs[key] = true;
    else delete errs[key];
    ctrl.setErrors(Object.keys(errs).length ? errs : null);
  }
}
